import React, { useState, useEffect } from "react";
import { Button } from "../../../UIElements/Button";
import {
    Box,
    Container,
    Typography,
    CircularProgress,
    Switch
} from "@mui/material";
import { Link } from "react-router-dom";
import cardPayment from "../../../../assets/img/cardPayment.svg";
import { loadStripe } from "@stripe/stripe-js";
import { stripeCheckoutMonthly, stripeCheckoutYearly,getCountOfQuestionsByUniversityAndCourse } from "../../../../api/auth";
import { useSelector } from "react-redux";
import "./Pricing.css";

const stripePromise = loadStripe("pk_live_51P7OvaRrXxqfPmR1caTAZrt2bxS3agOyJuYgNUs51OfYgGvaGn1EHYKw2BzEkI4aSkwbwfNXDLyrJxyYt7FDS9jf00HhbKhnJq");

const PaymentPage = () => {
    const university = useSelector((state) => state.auth.universityName);
    const course = useSelector((state) => state.auth.courseName);
    const [isAnnual, setIsAnnual] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const [questionCounts, setQuestionCounts] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [checkoutDisabled, setCheckoutDisabled] = useState(null);
    const userId = useSelector((state) => state.auth.userId);

useEffect(() => {
    const fetchQuestionCounts = async () => {
        try {
            const response = await getCountOfQuestionsByUniversityAndCourse(course, university);
            setQuestionCounts(response.data);
            setLoading(false);
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    };

    fetchQuestionCounts();
}, []);


    const handleCheckoutMonthly = async () => {
    try {
        if (!userId) {
            console.error("UserId is not available.");
            return;
        }

        // Disable button immediately
        setCheckoutDisabled(true); // Add this state to your component

        const session = await stripeCheckoutMonthly(userId, university, course);
        if (session.data.sessionId) {
            setSessionId(session.data.sessionId);
            const stripe = await stripePromise;
            const result = await stripe.redirectToCheckout({
                sessionId: session.data.sessionId,
            });

            if (result.error) {
                console.error(result.error.message);
                setCheckoutDisabled(false); // Re-enable on error
            }
        }
    } catch (error) {
        console.error('Error:', error);
        setCheckoutDisabled(false); // Re-enable on error
    }
};

    const handleCheckoutYearly = async () => {
        try {
            if (!userId) {
                console.error("UserId is not available.");
                return;
            }

            const session = await stripeCheckoutYearly(userId,university,course);
            if (session.data.sessionId) {
                setSessionId(session.data.sessionId);
                const stripe = await stripePromise;
                const result = await stripe.redirectToCheckout({
                    sessionId: session.data.sessionId,
                });

                if (result.error) {
                    console.error(result.error.message);
                }
            }
        } catch (error) {
            console.error('Error:', error);
        }
    };

    const buttonOnClick = isAnnual ? handleCheckoutYearly : handleCheckoutMonthly;

    const roundDownToNearest10 = (num) => {
        return Math.floor(num / 10) * 10;
    };

    if (loading) {
        return (
            <Container className="paymentContainer">
                <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="paymentContainer">
                <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
                    <Typography variant="h6">Error loading question data: {error}</Typography>
                </Box>
            </Container>
        );
    }

    if (questionCounts?.total_count === 0) {
        return (
            <Container className="paymentContainer">
                <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="80vh">
                    <Typography variant="h4" gutterBottom>
                        Question Bank Coming Soon!
                    </Typography>
                    <Typography variant="body1">
                        We're currently working on adding questions for {university}.
                    </Typography>
                    <Typography variant="body1">
                        Check back soon!
                    </Typography>
                </Box>
            </Container>
        );
    }

   const features = {
    monthly: [
        {
            title: "Complete Access",
            price: "6.99 GBP",
            details: [
                `${roundDownToNearest10(questionCounts?.total_count || 0)}+ Questions`,
                ...(questionCounts?.type2_count > 0 ? [
                    `${roundDownToNearest10(questionCounts.type2_count)}+ VSAQs`
                ] : []),
                ...(questionCounts?.type2_count > 0 ? ["Comprehensive SAQs"] : [])
            ].filter(Boolean), // This removes any empty strings
        },
    ],
    annual: [
        {
            title: "Complete Access",
            price: "20.99 GBP",
            details: [
                `${roundDownToNearest10(questionCounts?.total_count || 0)}+ Questions`,
                ...(questionCounts?.type2_count > 0 ? [
                    `${roundDownToNearest10(questionCounts.type2_count)}+ VSAQs`
                ] : []),
                ...(questionCounts?.type2_count > 0 ? ["Comprehensive SAQs"] : [])
            ].filter(Boolean),
        },
    ],
};

    const selectedFeatures = isAnnual ? features.annual : features.monthly;

    return (
        <Container className="paymentContainer">
            <Box display="flex" justifyContent="center" alignItems="center" marginY={2}>
                <div style={{ marginRight: "8px" }}>
                    <Switch
                        checked={isAnnual}
                        onChange={() => setIsAnnual(!isAnnual)}
                        name="pricingToggle"
                        color="primary"
                        mr={2}
                    />
                </div>
            </Box>

            <div className="pricing__section">
                <div className="pricing__wrapper">
                    <div className="pricing__container">
                        <h1 className="pricing__heading">
                            {isAnnual ? "Annual" : "Monthly"}
                        </h1>
                        {selectedFeatures.map((plan, index) => (
                            <Link
                                to="/register"
                                key={index}
                                className="pricing__container-card"
                            >
                                <div className="pricing__container-cardInfo">
                                    <div className="icon">
                                        <img
                                            src={cardPayment}
                                            alt="card payment"
                                            style={{ width: 100, height: 100 }}
                                        />
                                    </div>
                                    <h3>{plan.title}</h3>
                                    <h4>{plan.price}</h4>
                                    <p style={{ fontStyle: "italic" }}>
                                        {isAnnual ? "per year" : "per month"}
                                    </p>
                                    <ul className="pricing__container-features">
                                        {plan.details.map((detail, idx) => (
                                            <li key={idx}>{detail}</li>
                                        ))}
                                    </ul>
                                    <Button buttonSize="btn--wide" onClick={buttonOnClick}>
                                        Choose Plan
                                    </Button>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </div>
        </Container>
    );
};

export default PaymentPage;